import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, Compass, Home, Mail, FolderKanban, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const QUICK_LINKS = [
  { href: "/", label: "Accueil", icon: Home },
  { href: "/app/today", label: "Mon tableau de bord", icon: Compass },
  { href: "/app/mail", label: "Mes mails", icon: Mail },
  { href: "/app/projects", label: "Mes projets", icon: FolderKanban },
  { href: "/contact", label: "Contacter le support", icon: HelpCircle },
];

export default function NotFound() {
  const [location] = useLocation();

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg text-center">
        {/* Animated 404 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mb-8 inline-block"
        >
          <span
            className="text-[120px] md:text-[160px] font-bold tracking-tighter leading-none select-none"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary)/0.15) 0%, hsl(var(--primary)/0.05) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            404
          </span>
          {/* Floating compass icon */}
          <motion.div
            animate={{ y: [0, -8, 0], rotate: [0, 8, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-lg">
              <Compass className="w-8 h-8 text-primary" />
            </div>
          </motion.div>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Cette page est introuvable
          </h1>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-sm mx-auto">
            L'adresse{" "}
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono break-all">
              {location}
            </code>{" "}
            n'existe pas ou a été déplacée.
          </p>
        </motion.div>

        {/* CTA principal */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-8"
        >
          <Link href="/">
            <Button size="lg" className="gap-2 h-11 px-6">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </motion.div>

        {/* Liens rapides */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12"
        >
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">
            Ou accédez directement à
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
            {QUICK_LINKS.map(({ href, label, icon: Icon }, i) => (
              <motion.div
                key={href}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.45 + i * 0.06 }}
              >
                <Link href={href}>
                  <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3 text-sm hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer group">
                    <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    <span className="font-medium group-hover:text-foreground transition-colors">
                      {label}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Logo footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 flex items-center justify-center gap-2 text-muted-foreground text-sm"
        >
          <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
            O
          </div>
          <span className="font-semibold text-foreground">OneNext</span>
        </motion.div>
      </div>
    </div>
  );
}
